import type { FC } from "react";
import { useMemo } from "react";

import { Dropdown, DropdownButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { MAX_SELECTION } from "../../../../consts";
import { createCategories } from "../utils";

import CheckboxDropdownItem from "./CheckboxDropdownItem";
import { Container } from "./styledComponents";

interface CategoryCheckboxDropdownProps {
    selectedLobs: string[];
    handleSelectedLobsChange: (newSelectedLobs: string[]) => void;
    handleDropdownClose: () => void;
    availableLobs: string[];
    lobVisitsMap: Record<string, number>;
}

const CategoryCheckboxDropdown: FC<CategoryCheckboxDropdownProps> = ({
    availableLobs,
    selectedLobs,
    handleSelectedLobsChange,
    handleDropdownClose,
    lobVisitsMap,
}) => {
    const handleSelectionChange = (lobId: string, shouldSelect: boolean) => {
        if (shouldSelect && selectedLobs.length >= MAX_SELECTION) {
            return;
        }

        const newSelectedLobs = shouldSelect 
            ? [...selectedLobs, lobId]
            : selectedLobs.filter((id) => id !== lobId);
            
        handleSelectedLobsChange(newSelectedLobs);
    };

    const handleDropdownToggle = (isOpen: boolean) => {
        allTrackers.dispatchTrackEvent("dropdown", isOpen ? "open" : "close", "total visits");
        if (!isOpen) {
            handleDropdownClose();
        }
    };

    const handleItemClick = (item) => {
        const isCurrentlySelected = selectedLobs.includes(item.id);
        const shouldSelect = !isCurrentlySelected;

        if (shouldSelect && selectedLobs.length >= MAX_SELECTION) {
            return;
        }

        allTrackers.dispatchTrackEvent("dropdown", shouldSelect ? "checked" : "unchecked", "total visits", {
            selectedItem: item.id,
        });

        handleSelectionChange(item.id, shouldSelect);
    };

    const categories = useMemo(
        () => createCategories(availableLobs, lobVisitsMap, selectedLobs),
        [availableLobs, lobVisitsMap, selectedLobs],
    );

    const selectedIds = useMemo(
        () => Object.fromEntries(availableLobs.map((lob) => [lob, selectedLobs.includes(lob)])),
        [availableLobs, selectedLobs],
    );

    if (!categories.length) {
        return null;
    }

    return (
        <Container>
            <Dropdown
                selectedIds={selectedIds}
                onClick={handleItemClick}
                closeOnItemClick={false}
                hasSearch={true}
                searchPlaceHolder={translate("analysis.content.lob.dropdown.search.placeholder")}
                dropdownPopupMinScrollHeight={48}
                dropdownPopupHeight={300}
                onToggle={handleDropdownToggle}
            >
                {[
                    <DropdownButton>{translate("analysis.content.lob.title")}</DropdownButton>,
                    ...categories.map((item) => (
                        <CheckboxDropdownItem
                            key={item.id}
                            id={item.id}
                            text={item.text}
                            visitsText={item.visitsText}
                            isChecked={item.isChecked}
                            isDisabled={item.isDisabled}
                            onClick={() => handleItemClick(item)}
                        />
                    )),
                ]}
            </Dropdown>
        </Container>
    );
};

export default CategoryCheckboxDropdown;
